import { nanoid } from 'nanoid'

export type ActionPool = Array<{
  id: string
  action: () => void
  listeners: { click: (e: Event) => void; keyup: (e: KeyboardEvent) => void }
}>
type Binding = {
  id?: string
  value: () => unknown
  modifiers: Record<string, boolean>
}

function onClick(
  id: string,
  actionPool: ActionPool,
  el: HTMLElement,
  e: Event
) {
  const ignore = [...document.querySelectorAll('[data-outside-ignore]')]

  if (
    e.target === el ||
    ignore.some(i => e.target === i) ||
    el.contains(e.target as Node) ||
    ignore?.some(i => i.contains(e.target as Node))
  ) {
    return
  }

  const lastAction = actionPool.at(-1)

  if (lastAction?.id === id) {
    lastAction.action()
    window.removeEventListener('click', lastAction.listeners.click, true)
    window.removeEventListener('keyup', lastAction.listeners.keyup)
    actionPool.pop()
  }
}

const onKeyUp = (id: string, actionPool: ActionPool, e: KeyboardEvent) => {
  if (e.key === 'Escape') {
    const lastAction = actionPool.at(-1)

    if (lastAction?.id === id) {
      lastAction.action()
      window.removeEventListener('click', lastAction.listeners.click, true)
      window.removeEventListener('keyup', lastAction.listeners.keyup)
      actionPool.pop()
    }
  }
}

export default function outside(actionPool: ActionPool) {
  return {
    beforeMount(el: HTMLElement, binding: Binding) {
      if (!binding.value) {
        return
      }

      const modifiers = Object.keys(binding.modifiers).length
        ? binding.modifiers
        : { click: true, esc: true }
      const id = nanoid()
      const click = onClick.bind(null, id, actionPool, el)
      const keyup = onKeyUp.bind(null, id, actionPool)

      modifiers.click && window.addEventListener('click', click, true)
      modifiers.esc && window.addEventListener('keyup', keyup)

      binding.id = id
      actionPool.push({
        id,
        action: binding.value,
        listeners: { click, keyup },
      })
    },

    beforeUnmount(el: HTMLElement, binding: Binding) {
      const action = actionPool.find(a => a.id === binding.id)

      if (action) {
        window.removeEventListener('click', action.listeners.click, true)
        window.removeEventListener('keyup', action.listeners.keyup)
      }
    },
  }
}
